/* ═══════════════════════════════════════════════════════════════════════
   The server is the only thing this app talks to.

   No API keys here, no prompts, no model. The browser sends what the
   customer said and renders what comes back — which is what lets the same
   engine drive a phone call without the two drifting apart.
   ═══════════════════════════════════════════════════════════════════════ */

export interface Language {
  code: string;
  label: string;
  native: string;
  dir: "ltr" | "rtl";
  voice: boolean;
}

export interface FieldView {
  name: string;
  label: string;
  type: "text" | "email" | "tel" | "date" | "number" | "choice";
  choices: string[] | null;
  required: boolean;
  /**
   * A value the software uses, never one a person reads back.
   *
   * Still a real field — it is written to and stored like any other — but
   * its value is an id or a token that means nothing outside the code, so
   * no screen prints it and no form offers a box for it.
   */
  internal: boolean;
}

export interface ChecklistView {
  id: string;
  title: string;
  bucket: string | null;
  noneId: string | null;
  options: { id: string; label: string; hint: string | null }[];
}

export interface SecureGroupView {
  id: string;
  title: string;
  why: string;
  required: boolean;
  fields: { key: string; placeholder: string; type: string }[];
}

export type PageBlock =
  | { kind: "about"; title?: string; body: string }
  | { kind: "services"; title?: string; items: { label: string; body?: string }[] }
  | { kind: "facts"; title?: string; rows: { label: string; value: string }[] }
  | { kind: "questions"; title?: string; items: { q: string; a: string }[] }
  | { kind: "notice"; body: string };

export interface PageConfig {
  headline?: string;
  intro?: string;
  blocks?: PageBlock[];
}

export interface AssistantConfig {
  name?: string;
  greeting?: string;
  avatar?: string;
}

export interface PackView {
  id: string;
  version: string;
  displayName: string;
  /** The distinct jobs this business does. Empty for a single-workflow pack. */
  services: { id: string; label: string }[];
  /** The one this case is for, once crAIg has worked it out. */
  service: { id: string; label: string } | null;
  fields: FieldView[];
  checklists: ChecklistView[];
  secureGroups: SecureGroupView[];
  documents: { prompt: string; never: string[]; accept: string; maxBytes: number };
  /* The business's own page. Plain text throughout, never markup — see
     PageBlock in core/overlay.ts for why that is not negotiable. */
  page: PageConfig | null;
  assistant: AssistantConfig | null;
  declaration: {
    statement: string;
    basis: string;
    additional: { id: string; text: string; label: string }[];
  };
  /**
   * Every industry-specific word the interface says out loud. The web app
   * knows nothing about tax — a plumbing pack changes these strings and the
   * same screens read correctly.
   */
  copy: {
    invitation: string;
    languagePrompt?: string;
    languageNote?: string;
    reviewerNoun: string;
    reviewerNounPlural?: string;
    secureNote: string;
    evidence?: { threshold: number; under: string; over: string; perItem: string };
  };
}

export interface LineItemView {
  sourceId: string;
  what: string;
  amount?: string;
  when?: string;
  hasEvidence?: boolean;
  detail?: string;
  needsCheck?: boolean;
}

export interface CaseView {
  id: string;
  status: string;
  language: string;
  service: { id: string; label: string } | null;
  /**
   * Set when this business can't take the job. The intake is over — the
   * reason is the client's answer, not an error.
   */
  declined: { rule: string; reason: string; at: string } | null;
  /**
   * That a person has been asked for. Never why: the reason is written for
   * the business, and some of them would be upsetting to read about yourself.
   */
  escalation: { urgency: "now" | "today" | "soon"; at: string } | null;
  fields: Record<string, string>;
  checklists: Record<string, string[]>;
  asked: string[];
  items: Record<string, LineItemView[]>;
  topicNotes: Record<string, string>;
  secureProvided: Record<string, boolean>;
  documents: { id: string; name: string; mime: string }[];
  consent: { signedName: string; signedAt: string } | null;
  reviewed: { at: string } | null;
  /** Everything is collected and we're waiting on them to check it. */
  needsReview: boolean;
  progress: number;
  canSign: boolean;
  documentsUnlocked: boolean;
  remainingQuestions: number;
  flags: { ref: string; text: string }[];
  /**
   * What the business's own chat will be able to do — a setup conversation
   * only, and `null` for every real customer.
   *
   * Null and empty mean different things and the interface must keep them
   * apart: null is "this isn't a setup, render nothing at all", empty is
   * "it is, but nothing is settled yet", which gets a line of its own.
   */
  willHave: ChatModule[] | null;
  /**
   * The whole setup, for the drawer a business publishes from — and `null`
   * for every real customer, exactly like `willHave`.
   *
   * Kept separate from `willHave` rather than swallowing it, because the
   * customer intake renders those chips and has no business being handed a
   * slug, a contact address or a publish check.
   */
  summary: ChatSummary | null;
  /**
   * Something they have to read and agree to, when the business has one.
   * Null for almost everybody.
   *
   * `text` is what was SAVED when it was shown, not the business's current
   * wording — so editing the form tomorrow does not change what this
   * person read. See components/Presenter.tsx.
   */
  presenter: {
    title: string;
    why: string | null;
    text: string;
    mustAgree: boolean;
    agreedAt: string | null;
  } | null;
}

/** One thing the business's chat will be able to do. See server/preview.ts. */
export interface ChatModule {
  id: string;
  /** Two or three words. Short enough for a chip. */
  label: string;
  /** The module's own sentence, in words a plumber would use. */
  summary: string;
  /** The pack brings it regardless; false means something they said added it. */
  fromPack: boolean;
}

/**
 * Everything the publish drawer shows. See server/preview.ts.
 *
 * Every field of it comes out of the same `build()` that runs at publish,
 * so this is the chat itself rather than a description of one.
 */
export interface ChatSummary {
  /** What crAIg will be called on their page, when they renamed him. */
  assistantName: string | null;
  businessName: string | null;
  /** `letcraig.ai/<slug>`, once they have picked one. */
  address: string | null;
  /** The starting point they chose, by the name they chose it under. */
  template: string | null;
  /** What their customers will be offered, in the business's own words. */
  handles: string[];
  /** Questions the chat will be able to answer out loud, as asked. */
  answers: string[];
  collects: { label: string; required: boolean }[];
  /** Work it will turn away. These are rules code enforces, not judgement. */
  declines: string[];
  modules: ChatModule[];
  /** Anything crAIg could not turn into configuration, for their eye. */
  notes: string[];
  /**
   * What stands between this and being live, in their language. Empty
   * means the server would accept it — this is the publish route's own
   * list, not a second opinion about it.
   */
  blocking: string[];
  signed: boolean;
}

export interface TenantView {
  slug: string;
  displayName: string;
  branding: {
    accent?: string;
    /** One of the named schemes. `accent` wins if both are set. */
    scheme?: string;
    logoUrl?: string;
    tagline?: string;
    /** Whether crAIg's face keeps his colour here, or takes theirs. */
    face?: "craig" | "brand";
    /** How the conversation itself looks. Constrained — see core/schemes.ts. */
    chat?: { surface?: string; font?: string; bubbles?: "accent" | "quiet" };
  };
}

export interface Message {
  role: "client" | "craig";
  text: string;
  channel: "chat" | "voice";
  at: string;
  secureLabel?: string;
}

async function json<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return res.json() as Promise<T>;
}

export const api = {
  languages: () =>
    fetch("/api/languages").then((r) =>
      json<{ languages: Language[]; voiceAvailable: boolean }>(r),
    ),

  tenant: (slug: string) =>
    fetch(`/api/tenants/${encodeURIComponent(slug)}`).then((r) =>
      json<{ tenant: TenantView; pack: PackView }>(r),
    ),

  createCase: (tenantSlug: string, language: string) =>
    fetch("/api/cases", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantSlug, language }),
    }).then((r) => json<{ case: CaseView; pack: PackView }>(r)),

  getCase: (id: string) =>
    fetch(`/api/cases/${id}`).then((r) =>
      json<{ case: CaseView; pack: PackView; transcript: Message[] }>(r),
    ),

  /** The five purposes and the worked starting points. Open — no account. */
  options: () =>
    fetch("/api/account/options").then((r) =>
      json<{
        purposes: { id: string; label: string; hint: string }[];
        templates: { id: string; label: string; blurb: string; group: string | null }[];
      }>(r),
    ),

  /**
   * Picking a starting point on the builder's panel.
   *
   * Returns the pack as well as the case, because a template decides which
   * pack the chat runs on — so the form beside the conversation is now a
   * different form, and redrawing it from the old one would show questions
   * that no longer apply.
   */
  chooseTemplate: (id: string, template: string) =>
    fetch(`/api/cases/${id}/template`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ template }),
    }).then((r) => json<{ case: CaseView; pack: PackView }>(r)),

  /** They read it and agreed. Silent — no model call, no turn. */
  agreePresenter: (id: string) =>
    fetch(`/api/cases/${id}/presenter`, { method: "POST" }).then((r) =>
      json<{ case: CaseView }>(r),
    ),

  correctField: (id: string, fields: Record<string, string>) =>
    fetch(`/api/cases/${id}/fields`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(fields),
    }).then((r) => json<{ case: CaseView }>(r)),

  /**
   * A tick on the settings panel. Silent, instant, and free — no model
   * call. crAIg is brought in afterwards for the whole burst, once they
   * have stopped clicking. See the Builder.
   */
  setChecklists: (id: string, checklists: Record<string, string[]>) =>
    fetch(`/api/cases/${id}/checklists`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ checklists }),
    }).then((r) => json<{ case: CaseView }>(r)),

  /**
   * Secure values leave the browser once and never come back. The response
   * is the boolean map, so the tick appears without the value being held
   * anywhere in the page after this call returns.
   */
  saveSecure: (id: string, group: string, values: Record<string, string>) =>
    fetch(`/api/cases/${id}/secure`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ group, values }),
    }).then((r) => json<{ case: CaseView }>(r)),

  /**
   * Everything goes into one bucket, unsorted. Partial success is normal —
   * a customer selecting twenty photos may include one the phone won't
   * hand over, and that must not lose the other nineteen.
   */
  uploadDocuments: (id: string, files: File[], slot?: string) => {
    const form = new FormData();
    for (const f of files) form.append("file", f, f.name);
    // The slot travels separately from the file. A filename is not evidence
    // of anything — IMG_4471.jpg is as likely to be a licence as a meter box.
    const q = slot ? `?slot=${encodeURIComponent(slot)}` : "";
    return fetch(`/api/cases/${id}/documents${q}`, { method: "POST", body: form }).then((r) =>
      json<{ case: CaseView; added: string[]; rejected: { name: string; why: string }[] }>(r),
    );
  },

  /** "I've checked this and it's right." Separate act from signing. */
  review: (id: string, confirmedDespite: string[]) =>
    fetch(`/api/cases/${id}/review`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ confirmedDespite }),
    }).then((r) => json<{ case: CaseView }>(r)),

  sign: (
    id: string,
    body: { signedName: string; signatureImage: string; additional: Record<string, boolean> },
  ) =>
    fetch(`/api/cases/${id}/declaration`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }).then((r) => json<{ case: CaseView }>(r)),

  /**
   * Publish: send the one-click link to the address they gave us.
   *
   * This does NOT put the chat live, and the button must never say it
   * does. Nothing exists until the link in that email is clicked, which is
   * what makes the address theirs rather than anyone's — a stranger can
   * type any email into a form, and only clicking through it proves the
   * inbox is theirs.
   */
  confirmSetup: (id: string) =>
    fetch(`/api/onboarding/${id}/confirm`, { method: "POST" }).then((r) =>
      json<{ sent: boolean; to: string; via: string }>(r),
    ),
};

/* ── A turn ─────────────────────────────────────────────────────────────
   Server-sent events rather than a plain POST, so the reply appears as it
   is written instead of arriving in one lump after several seconds. */

export interface TurnDone {
  say: string;
  suggestions: string[];
  inputHint: "date" | "email" | "tel" | "number" | null;
  secureRequest: string | null;
  /**
   * Module surfaces crAIg just asked for — an upload button for a named
   * document, a letter to read. Core names them; this app knows what each
   * one looks like.
   */
  opened?: string[];
  case: CaseView;
}

export interface TurnHandlers {
  onDelta(text: string): void;
  onDone(result: TurnDone): void;
  onError(message: string, detail?: string): void;
}

export async function runTurn(
  caseId: string,
  text: string,
  handlers: TurnHandlers,
  signal?: AbortSignal,
  /**
   * What they ticked, when the answer came off a list rather than the
   * keyboard. Sent as IDS alongside the sentence, so the server records
   * the answer itself instead of asking the recording call to work it out
   * again from prose it was handed exactly.
   */
  /**
   * Which settings lists they have just been editing on the panel, by id.
   * Ids only — the server writes the sentence crAIg actually reads, so a
   * browser can never put words into his instructions.
   */
  panelChanged?: string[],
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`/api/cases/${caseId}/turn`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        text,
        ...(panelChanged?.length ? { panelChanged } : {}),
      }),
      ...(signal ? { signal } : {}),
    });
  } catch {
    handlers.onError("Couldn't reach the server. Check your connection and try again.");
    return;
  }

  if (!res.ok || !res.body) {
    /* The server's own words, when it has any.
       ─────────────────────────────────────────────────────
       Every non-200 used to collapse into "tap send to try again", which
       threw away messages written specifically for the person reading
       them — running out of credit mid-conversation has a careful,
       blameless explanation on the server that no customer has ever seen.

       And the generic line is actively wrong for all of them: retrying a
       closed case, or a message too long to accept, fails identically
       every time. Telling somebody to try again forever is worse than
       telling them nothing. */
    const said = await res
      .json()
      .then((b: { error?: string }) => (typeof b.error === "string" ? b.error : null))
      .catch(() => null);
    handlers.onError(said ?? "Sorry — that didn't go through. Tap send to try again.");
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      // Frames are separated by a blank line, per the SSE wire format.
      let split: number;
      while ((split = buffer.indexOf("\n\n")) >= 0) {
        const frame = buffer.slice(0, split);
        buffer = buffer.slice(split + 2);

        const event = /^event: (.+)$/m.exec(frame)?.[1];
        const raw = /^data: (.+)$/m.exec(frame)?.[1];
        if (!event || !raw) continue;

        const data = JSON.parse(raw);
        if (event === "delta") handlers.onDelta(data.text);
        else if (event === "done") handlers.onDone(data as TurnDone);
        else if (event === "error") handlers.onError(data.message, data.detail);
      }
    }
  } catch (err) {
    // An abort is the customer closing the tab or hitting stop — not a fault.
    if ((err as Error)?.name !== "AbortError") {
      handlers.onError("The connection dropped part-way through. Tap send to try again.");
    }
  }
}
